import { applyI18n, t } from '../i18n';
import {
  connectSmsDeliveryLive,
  disconnectSmsDeliveryLive,
  holdSmsDeliveryLive,
  type SmsDeliveryUpdatedMessage,
} from '../ws/smsDeliveryLive';
import { catchUpSmsDeliveryStatus } from '../ws/smsDeliveryCatchUp';
import { registerShareLinkDialogWatcher } from '../ws/infoShareLive';
import { buildSmsStatusFlow, type SmsFlowStatus } from './smsStatusFlow';
import { buildSmsDevSimulateControls, loadUsesDevLogSink } from './smsDevSimulateControls';
import { showLinkCopyDialog } from './linkCopyDialog';

export interface ShareLinkCreateResult {
  guestUrl: string;
  shareId: string;
  multiUseAccess: boolean;
  smsDeliveryReference?: string;
  smsPhoneNumber?: string;
}

export interface ShareLinkCreateHooks {
  /** Connect live updates before the create API returns (avoids missed DLRs). */
  onSmsDeliveryReferenceReady: (reference: string) => void | Promise<void>;
}

export interface ShareLinkDialogOptions {
  onSmsDeliveryUpdate?: (message: SmsDeliveryUpdatedMessage) => void;
}

export async function showShareLinkDialog(
  createShare: (hooks: ShareLinkCreateHooks) => Promise<ShareLinkCreateResult>,
  options?: ShareLinkDialogOptions,
): Promise<void> {
  let created: ShareLinkCreateResult | null = null;
  let smsStatus: SmsFlowStatus = 'pending';
  let sawAccepted = false;
  let smsErrorDescription: string | null = null;
  let smsHost: HTMLElement | null = null;
  let ownsSmsLive = false;
  let usesDevLogSink = false;
  let earlySmsReference: string | null = null;

  const statusRank = (status: SmsFlowStatus): number => {
    switch (status) {
      case 'pending':
        return 0;
      case 'accepted':
        return 1;
      case 'delivered':
      case 'failed':
        return 2;
    }
  };

  const applyCatchUp = async (reference: string): Promise<void> => {
    try {
      const catchUp = await catchUpSmsDeliveryStatus(reference);
      // Never regress UI if a live WS update already advanced further.
      if (statusRank(catchUp.status) < statusRank(smsStatus)) {
        return;
      }
      if (catchUp.status === 'delivered') {
        smsStatus = 'delivered';
        smsErrorDescription = null;
      } else if (catchUp.status === 'accepted') {
        smsStatus = 'accepted';
        sawAccepted = true;
        smsErrorDescription = catchUp.errorDescription;
      } else if (catchUp.status === 'failed') {
        smsStatus = 'failed';
        smsErrorDescription = catchUp.errorDescription;
      } else {
        smsStatus = 'pending';
        smsErrorDescription = catchUp.errorDescription;
      }
      renderSmsStatus();
      options?.onSmsDeliveryUpdate?.({
        type: 'sms.deliveryUpdated',
        reference,
        status: catchUp.status,
        cmStatusCode: catchUp.cmStatusCode,
        errorCode: null,
        errorDescription: catchUp.errorDescription,
        destinationCiphertextBase64: '',
        updatedAt: new Date().toISOString(),
        failedAt: catchUp.status === 'failed' ? new Date().toISOString() : null,
      });
    } catch {
      // Keep pending UI if catch-up fails.
    }
  };

  const renderSmsStatus = (): void => {
    if (!smsHost || !created?.smsDeliveryReference) {
      return;
    }

    smsHost.replaceChildren();
    const heading = document.createElement('p');
    heading.className = 'muted';
    heading.setAttribute('data-i18n', 'info.owner.smsDeliveryHeading');
    heading.textContent = t('info.owner.smsDeliveryHeading');

    const phone = document.createElement('p');
    phone.className = 'sms-status-list__phone';
    phone.textContent = created.smsPhoneNumber ?? '';

    smsHost.append(heading, phone, buildSmsStatusFlow(smsStatus, sawAccepted, smsErrorDescription));
    if (usesDevLogSink) {
      smsHost.append(buildSmsDevSimulateControls(created.smsDeliveryReference));
    }
    applyI18n(smsHost);
  };

  const handleSmsUpdate = (message: SmsDeliveryUpdatedMessage): void => {
    const watchedReference = created?.smsDeliveryReference ?? earlySmsReference;
    if (watchedReference && message.reference === watchedReference) {
      if (message.status === 'delivered') {
        smsStatus = 'delivered';
        smsErrorDescription = null;
      } else if (message.status === 'accepted') {
        smsStatus = 'accepted';
        sawAccepted = true;
        smsErrorDescription = message.errorDescription;
      } else if (message.status === 'failed') {
        smsStatus = 'failed';
        smsErrorDescription = message.errorDescription;
      } else if (message.status === 'pending') {
        smsStatus = 'pending';
        smsErrorDescription = message.errorDescription;
      }

      renderSmsStatus();
    }

    options?.onSmsDeliveryUpdate?.(message);
  };

  const connectEarly = async (reference: string): Promise<void> => {
    earlySmsReference = reference;
    ownsSmsLive = true;
    holdSmsDeliveryLive(true);
    const socket = connectSmsDeliveryLive({ onUpdate: handleSmsUpdate });
    try {
      await socket.whenConnected(10_000);
    } catch {
      // Proceed anyway; catch-up + later reconnect may still recover status.
    }
  };

  void loadUsesDevLogSink().then((value) => {
    usesDevLogSink = value;
    if (smsHost) {
      renderSmsStatus();
    }
  });

  try {
    await showLinkCopyDialog(
      async () => {
        created = await createShare({
          onSmsDeliveryReferenceReady: connectEarly,
        });
        if (created.smsDeliveryReference) {
          if (!ownsSmsLive) {
            await connectEarly(created.smsDeliveryReference);
          }
          void applyCatchUp(created.smsDeliveryReference);
        }
        return created.guestUrl;
      },
      {
        titleKey: 'info.owner.dialogTitle',
        warningKey: 'info.owner.linkWarning',
        createdKey: 'info.owner.created',
        creatingTitleKey: 'info.owner.creating',
        creatingDetailKey: 'info.owner.creatingDetail',
      },
      {
        onReady: ({ close, body }) => {
          if (created?.smsDeliveryReference) {
            smsHost = document.createElement('div');
            smsHost.className = 'link-copy-sms-status';
            body.append(smsHost);
            renderSmsStatus();
            if (!ownsSmsLive) {
              void connectEarly(created.smsDeliveryReference);
            }
          }

          if (created && created.shareId && !created.multiUseAccess) {
            registerShareLinkDialogWatcher((message) => {
              if (message.shareId !== created!.shareId || message.displayStatus !== 'Opened') {
                return;
              }

              registerShareLinkDialogWatcher(null);
              close();
            });
          }
        },
      },
    );
  } finally {
    registerShareLinkDialogWatcher(null);
    if (ownsSmsLive) {
      holdSmsDeliveryLive(false);
      disconnectSmsDeliveryLive({ force: true });
    }
  }
}
